import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import ptBR from 'antd/locale/pt_BR';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { layoutTheme, darkTheme } from './styles/themes/layout-theme';
import { useAuthStore } from './store/authStore';
import { useAppStore } from './store/appStore';
import AppLayout from './components/Layout/AppLayout';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Dashboard from './pages/Dashboard';
import CompanyList from './pages/Companies/CompanyList';
import CompanyForm from './pages/Companies/CompanyForm';
import CompanyDetail from './pages/Companies/CompanyDetail';
import TokenManager from './pages/Companies/TokenManager';
import UserList from './pages/Users/UserList';
import UserForm from './pages/Users/UserForm';
import UserDetail from './pages/Users/UserDetail';
import DeviceList from './pages/Devices/DeviceList';
import DeviceDetail from './pages/Devices/DeviceDetail';
import DeviceForm from './pages/Devices/DeviceForm';
import SessionList from './pages/Sessions/SessionList';
import SessionDetail from './pages/Sessions/SessionDetail';
import AuditList from './pages/Audit/AuditList';
import AuditDetail from './pages/Audit/AuditDetail';

function ThemeEffect() {
  const resolvedTheme = useAppStore((state) => state.resolvedTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.profile === 'SUPER_ADMIN' || user?.profile === 'ADMIN';
  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" />;
}

function App() {
  const loadUser = useAuthStore((state) => state.loadUser);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const resolvedTheme = useAppStore((state) => state.resolvedTheme);

  useEffect(() => {
    if (isAuthenticated) {
      loadUser();
    }
  }, [isAuthenticated, loadUser]);

  const currentTheme = resolvedTheme === 'dark' ? darkTheme : layoutTheme;

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={ptBR} theme={currentTheme}>
        <ThemeEffect />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <AppLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
              <Route path="companies" element={<AdminRoute><CompanyList /></AdminRoute>} />
              <Route path="companies/new" element={<AdminRoute><CompanyForm /></AdminRoute>} />
              <Route path="companies/:id" element={<AdminRoute><CompanyDetail /></AdminRoute>} />
              <Route path="companies/:id/edit" element={<AdminRoute><CompanyForm /></AdminRoute>} />
              <Route path="companies/:id/token" element={<AdminRoute><TokenManager /></AdminRoute>} />
              <Route path="users" element={<AdminRoute><UserList /></AdminRoute>} />
              <Route path="users/new" element={<AdminRoute><UserForm /></AdminRoute>} />
              <Route path="users/:id" element={<AdminRoute><UserDetail /></AdminRoute>} />
              <Route path="users/:id/edit" element={<AdminRoute><UserForm /></AdminRoute>} />
              <Route path="devices" element={<DeviceList />} />
              <Route path="devices/new" element={<DeviceForm />} />
              <Route path="devices/:id" element={<DeviceDetail />} />
              <Route path="devices/:id/edit" element={<DeviceForm />} />
              <Route path="sessions" element={<SessionList />} />
              <Route path="sessions/:id" element={<SessionDetail />} />
              <Route path="audit" element={<AdminRoute><AuditList /></AdminRoute>} />
              <Route path="audit/:id" element={<AdminRoute><AuditDetail /></AdminRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
